import { CircularProgress, IconButton } from '@civilgrid/ui/components';
import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';
import { editOrganization } from '~/api/organizations';
import Input from '~/components2/forms/Input';
import Notify from '~/components2/Notify';
import rootStore from '~/store';
import PricingTable from './PricingTable';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function BillingComponent() {
  const [billingContacts, setBillingContacts] = useState<string[]>([]);
  const [inEditMode, setInEditMode] = useState(false);
  const [isValid, setIsValid] = useState(true);

  const { currentOrganizationDetail } = useSnapshot(rootStore) as typeof rootStore;

  useEffect(() => {
    if (!currentOrganizationDetail.isLoading) {
      setBillingContacts(currentOrganizationDetail.billingContacts);
    }
  }, [currentOrganizationDetail]);

  if (currentOrganizationDetail.isLoading) {
    return <CircularProgress indeterminate />;
  }

  const validateContacts = (contacts: string[]) => {
    const trimmedContacts = contacts.map((contact) => contact.trim());
    if (trimmedContacts.length === 0) {
      setIsValid(false);
      return;
    }
    if (!trimmedContacts.every((contact) => emailRegex.test(contact))) {
      setIsValid(false);
      return;
    }
    setIsValid(true);
  };

  const handleSaveBillingContacts = async () => {
    const trimmedContacts = billingContacts.filter((contact) => contact.trim().length > 0);

    await editOrganization(currentOrganizationDetail.id, {
      billingContacts: trimmedContacts,
    });

    Notify.info({
      title: 'Billing Contacts',
      message: 'Billing contacts successfully updated',
    });

    setInEditMode(false);
  };

  const onChange = (value: string) => {
    const contacts = value.split(',').map((contact) => contact.trim());
    setBillingContacts(contacts);
    validateContacts(contacts);
  };

  return (
    <div className="flex w-1/2 flex-col space-y-4">
      <div className="pl-1 text-base font-bold">Billing Contacts </div>
      <div className="pl-1 mb-4">
        <div className="flex flex-col space-y-0.5">
          <div className="text-sm">
            The billing contact will receive invoices from CivilGrid. Project Managers will also receive invoices for
            relevant projects. You can set the billing contact to an individual’s direct email, an AP, or an accounting
            mailbox.
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <table className="w-full">
          <thead>
            {billingContacts.length > 0 ? (
              <tr className="text-left text-sm font-bold">
                <th className="pl-1 text-sm">Email(s)</th>
              </tr>
            ) : (
              <tr>
                <th className="text-left">
                  <div className="flex flex-col space-y-1 text-sm">
                    No billing contact set. Click the icon to add a contact
                  </div>
                </th>
              </tr>
            )}
          </thead>
          <tbody>
            {inEditMode ? (
              <Input
                label=""
                value={billingContacts.join(',')}
                onChange={onChange}
                data-testid="org-billing-contacts-input"
              />
            ) : (
              billingContacts.map((contact, i) => (
                <tr
                  // biome-ignore lint/suspicious/noArrayIndexKey: legacy
                  key={`${i}-${contact}`}
                  className="hover:bg-secondary/5 group text-sm"
                >
                  <td className="pl-1 py-1 align-top">{contact}</td>
                  <td className="pl-1 py-1 align-top">{}</td>
                </tr>
              ))
            )}
            {!isValid && (
              <div className="pt-1 text-red-500 text-sm">
                Please enter valid email addresses in a comma-separated list
              </div>
            )}
          </tbody>
        </table>
        <div className="w-fit">
          {inEditMode ? (
            <IconButton
              icon="check"
              variant="primary"
              onClick={handleSaveBillingContacts}
              disabled={!isValid}
              size="small"
              data-testid="org-billing-contacts-save"
            >
              Save
            </IconButton>
          ) : (
            <IconButton
              icon="edit"
              variant="secondary"
              onClick={() => setInEditMode((prev) => !prev)}
              size="small"
              data-testid="org-billing-contacts-edit"
            >
              Edit
            </IconButton>
          )}
        </div>
      </div>
      <PricingTable subscription={currentOrganizationDetail.subscription} />
    </div>
  );
}
